import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardList, Upload } from "lucide-react";

export default function StudentAssignments() {
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [content, setContent] = useState({});
  const [enrolled, setEnrolled] = useState([]);

  const load = async () => {
    const [e, s] = await Promise.all([api.get("/enrollments/me"), api.get("/assignments/me/submissions")]);
    setEnrolled(e.data); setSubmissions(s.data);
    const courseIds = e.data.map((x) => x.course_id);
    const all = await api.get("/assignments");
    setAssignments(all.data.filter((a) => courseIds.includes(a.course_id)));
  };
  useEffect(() => { load(); }, []);

  const submit = async (id) => {
    try {
      const form = new FormData();
      form.append("content", content[id] || "");
      await api.post(`/assignments/${id}/submit`, form);
      toast.success("Submitted — great job!");
      setContent((c) => ({ ...c, [id]: "" }));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Submit failed"); }
  };

  const isSubmitted = (id) => submissions.some((s) => s.assignment_id === id);
  const scoreFor = (id) => submissions.find((s) => s.assignment_id === id)?.score;

  return (
    <Layout title="Assignments" subtitle="Student · Submit">
      <div className="space-y-4">
        {assignments.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-slate-500 text-sm">
            No assignments yet — enroll in a course to see assignments here.
          </div>
        )}
        {assignments.map((a) => (
          <div key={a.id} data-testid={`assignment-item-${a.id}`} className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <ClipboardList className="w-4 h-4 text-blue-600" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Assignment</span>
                </div>
                <h3 className="font-display font-bold text-lg text-slate-900">{a.title}</h3>
                <p className="text-sm text-slate-500 mt-1">{a.description}</p>
                <div className="text-xs text-slate-500 mt-2">Due {a.due_date} · {a.max_score} points</div>
              </div>
              {isSubmitted(a.id) && (
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 text-emerald-800 text-sm" data-testid={`score-${a.id}`}>
                  Scored {scoreFor(a.id)}/{a.max_score}
                </div>
              )}
            </div>
            {!isSubmitted(a.id) && (
              <div className="mt-4">
                <textarea
                  data-testid={`assn-content-${a.id}`}
                  value={content[a.id] || ""} onChange={(e) => setContent((c) => ({ ...c, [a.id]: e.target.value }))}
                  className="w-full min-h-[100px] p-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm"
                  placeholder="Paste your work / write your submission…"
                />
                <button data-testid={`submit-assn-${a.id}`} onClick={() => submit(a.id)} className="mt-3 px-4 py-2 rounded-full bg-blue-600 text-white text-sm hover:bg-blue-700 inline-flex items-center gap-1.5">
                  <Upload className="w-4 h-4" /> Submit
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Layout>
  );
}
